import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, Dialog, DialogContent, Box, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@mui/material';
import ErrorMessage from './ErrorMessage';
import '../styles/Form.scss';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ApiService from '../service/ApiService';

interface FormProps {
    onSuccess?: () => void;
}

function Form({ onSuccess }: FormProps) {
    const { control, handleSubmit, register, formState: { errors }, reset } = useForm();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (values: any) => {
        setLoading(true);
        try {
            await ApiService.createTask(values);
            if (onSuccess) {
                onSuccess();
            }
            reset();
            handleClose();
        } catch (error) {
            console.error("Failed to create task", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div>
            <IconButton aria-label="add" onClick={handleClickOpen}>
                <Alert icon={<AddIcon fontSize="inherit" />} sx={{ alignItems: 'center' }} severity="success">Add new task</Alert>
            </IconButton>
            <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={2} className="form-cont">
                        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                            <Box
                                component="input"
                                type="text"
                                placeholder="Name"
                                style={errors.name ? { borderColor: "red" } : {}}
                                {...register("name", { required: true, maxLength: 50 })}
                                className="name input"
                            />
                            <ErrorMessage error={errors.name} />

                            <Box
                                component="input"
                                type="text"
                                placeholder="Description"
                                style={errors.description ? { borderColor: "red" } : {}}
                                {...register("description", { required: true, maxLength: 100 })}
                                className="description input"
                            />
                            <ErrorMessage error={errors.description} />

                            <FormControl fullWidth style={errors.priority ? { border: "1px solid red" } : {}}>
                                <InputLabel id="priority-label">Priority</InputLabel>
                                <Controller
                                    name="priority"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="priority-label"
                                            label="Priority"
                                            className="priority-select input"
                                        >
                                            <MenuItem value="highest">Highest</MenuItem>
                                            <MenuItem value="high">High</MenuItem>
                                            <MenuItem value="medium">Medium</MenuItem>
                                            <MenuItem value="low">Low</MenuItem>
                                            <MenuItem value="lowest">Lowest</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                            <ErrorMessage error={errors.priority} />

                            <FormControl fullWidth style={errors.category ? { border: "1px solid red" } : {}}>
                                <InputLabel id="category-label">Category</InputLabel>
                                <Controller
                                    name="category"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="category-label"
                                            label="Category"
                                            className="category-select input"
                                        >
                                            <MenuItem value="cleaning">Cleaning</MenuItem>
                                            <MenuItem value="service">Service</MenuItem>
                                            <MenuItem value="maintaining">Maintaining</MenuItem>
                                            <MenuItem value="advising">Advising</MenuItem>
                                            <MenuItem value="checking">Checking</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                            <ErrorMessage error={errors.category} />

                            <Box >
                            <LocalizationProvider  dateAdapter={AdapterDateFns}>
                            <Controller
                            
                            name="targetDateRaw"
                            control={control}
                            defaultValue={null}
                            rules={{ required: "Target date is required" }}
                            render={({ field }) => (
                                <DateTimePicker
                                    className="date-picker"
                                    label="Target date"
                                    {...register("targetDateRaw", { required: false })}
                                    onChange={(date) => field.onChange(date)}
                                    format='yyyy-MM-dd HH:mm:ss'
                                    value={field.value}
                                />)} />
                                <ErrorMessage error={errors.targetDateRaw} />
                            </LocalizationProvider>
                            </Box>

                            <Box
                                component="input"
                                type="assignedTo"
                                placeholder="example@email.com"
                                style={errors.assignedTo ? { borderColor: "red" } : {}}
                                {...register("assignedTo", {
                                    required: true,
                                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                                })}
                                className="assigned-address input"
                            />
                            <ErrorMessage error={errors.assignedTo} />

                        </Box>
                        <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary" disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : 'Create'}
                        </Button>
                        <Button className="cancel" onClick={handleClose} variant="contained" color="warning" disabled={loading}>
                            Cancel
                        </Button>

                    </Box>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Form;